// src/api/analytics.api.ts

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit,
  Timestamp,
  doc,
  getDoc 
} from 'firebase/firestore';
import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { httpsCallable } from 'firebase/functions';

// Types pour l'analytics
export interface AnalyticsEvent {
  id?: string;
  eventType: 'page_view' | 'ticket_view' | 'ticket_add' | 'ticket_remove' | 'bus_view' | 'bus_add' | 'checkout_start' | 'checkout_complete' | 'share_event' | 'interested_click' | 'promo_code_apply' | 'insurance_toggle' | 'goodies_view' | 'goodies_add';
  eventId: string;
  userId?: string;
  sessionId: string;
  timestamp: Date;
  pageUrl: string;
  referrer: string | null;
  userAgent: string;
  country?: string;
  city?: string;
  device: 'mobile' | 'tablet' | 'desktop';
  browser: string;
  metadata?: Record<string, any>;
}

export interface AnalyticsOverview {
  eventId: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  totalViews: number;
  uniqueViews: number;
  totalUsers: number;
  authenticatedUsers: number;
  conversionRate: number;
  totalRevenue: number;
  averageOrderValue: number;
  bounceRate: number;
  averageSessionDuration: number;
  topCountries: Array<{ country: string; count: number }>;
  topDevices: { mobile: number; tablet: number; desktop: number };
  trafficSources: TrafficSourcesData;
  // New visitor metrics
  visitorTypes: {
    newVisitors: number;
    returningVisitors: number;
  };
  // Landing page analytics
  topLandingPages: Array<{
    path: string;
    count: number;
    percentage: number;
  }>;
  // Social platform breakdown
  socialPlatforms: Array<{
    platform: string;
    count: number;
    percentage: number;
  }>;
  // Top referrer domains
  topReferrerDomains: Array<{
    domain: string;
    count: number;
    percentage: number;
  }>;
  dailyStats: Array<{
    date: string;
    views: number;
    users: number;
    conversions: number;
    revenue: number;
  }>;
}

export interface TrafficSourcesData {
  direct: number;
  organicSearch: number;
  paidSearch: number;
  socialOrganic: number;
  socialPaid: number;
  email: number;
  referral: number;
  other: number;
  breakdown: Array<{
    source: string;
    type: string;
    count: number;
    percentage: number;
  }>;
}

export interface RealTimeData {
  eventId: string;
  currentViewers: number;
  todayViews: number;
  todayUsers: number;
  recentActions: AnalyticsEvent[];
  topReferrers: Array<{ source: string; count: number }>;
}

export interface ConversionFunnel {
  eventId: string;
  pageViews: number;
  ticketViews: number;
  ticketAdds: number;
  checkoutStarts: number;
  checkoutCompletes: number;
  conversionRates: {
    viewToTicket: number;
    ticketToAdd: number;
    addToCheckout: number;
    checkoutToComplete: number;
    overall: number;
  };
}

export interface TrafficSources {
  direct: number;
  social: number;
  search: number;
  email: number;
  paid: number;
  referral: number;
  other: Record<string, number>;
}

export interface ShareAnalytics {
  eventId: string;
  totalShares: number;
  uniqueSharers: number;
  sharesByMethod: Record<string, number>;
  totalConversions: number;
  conversionRate: number;
  totalAttributedRevenue: number;
  topSharers: Array<{
    userId: string;
    shares: number;
    conversions: number;
    revenue: number;
  }>;
  viralCoefficient: number;
  shareMultiplier: number;
}

export interface SessionFlowAnalytics {
  eventId: string;
  eventPageViews: number;
  checkoutEntries: number;
  participantStepCompletes: number;
  paymentStepEntries: number;
  paymentSuccesses: number;
  confirmationViews: number;
  sessionDurations: {
    short: number;
    medium: number;
    long: number;
    veryLong: number;
    total: number;
  };
  averageSessionDuration: number;
  completedSessions: number;
  abandonedSessions: number;
  totalSessions: number;
  dropoffReasons: {
    eventPageExit: number;
    checkoutAbandonment: number;
    paymentFailure: number;
    validationErrors: number;
  };
}

export interface CheckoutAnalytics {
  eventId: string;
  checkoutStarts: number;
  formCompletions: number;
  paymentAttempts: number;
  checkoutCompletions: number;
  stepAnalysis: Array<{
    step: string;
    users: number;
    completions: number;
    dropoffs: number;
  }>;
  checkoutSteps: Array<{
    stepName: string;
    usersReached: number;
    completed: number;
    averageTime: number;
    successRate: number;
    commonErrors: string[];
  }>;
}

export interface PaymentAnalytics {
  eventId: string;
  totalPaymentAttempts: number;
  successfulPayments: number;
  failedPayments: number;
  cancelledPayments: number;
  averagePaymentAmount: number;
  failureReasons: Array<{
    reason: string;
    count: number;
    percentage: number;
  }>;
}

export interface AnalyticsFilters {
  dateRange: {
    start: Date;
    end: Date;
  };
  countries?: string[];
  devices?: string[];
  trafficSources?: string[];
  userType?: 'all' | 'authenticated' | 'anonymous';
}

export const AnalyticsApi = createApi({
  reducerPath: 'AnalyticsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Analytics', 'RealTime', 'Sharing', 'ConversionFunnel'],
  endpoints: (builder) => ({
    
    // Récupérer la vue d'ensemble des analytics
    getAnalyticsOverview: builder.query<AnalyticsOverview, { eventId: string; filters?: AnalyticsFilters }>({
      async queryFn({ eventId, filters }) {
        try {
          const endDate = filters?.dateRange?.end || new Date();
          const startDate = filters?.dateRange?.start || new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);

          // Requête pour récupérer tous les événements analytics
          const eventsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate)),
            orderBy('timestamp', 'desc')
          );


          const eventsSnapshot = await getDocs(eventsQuery);
          const events = eventsSnapshot.docs.map(doc => {
            const data = doc.data();
            //console.log('Event Data:', data); // Debugging line to check event data
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          // Traitement des données
          const overview = await processAnalyticsOverview(events, eventId, startDate, endDate, filters);
          
          return { data: overview };
        } catch (error: any) {
          console.error('Error fetching analytics overview:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Analytics'],
    }),

    // Données temps réel
    getRealTimeAnalytics: builder.query<RealTimeData, string>({
      async queryFn(eventId) {
        try {
          const now = new Date();
          const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
          const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

          // Utilisateurs actifs (dernières 30 minutes)
          const activeViewersQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('eventType', '==', 'page_view'),
            where('timestamp', '>=', Timestamp.fromDate(thirtyMinutesAgo))
          );
          

          const activeViewersSnapshot = await getDocs(activeViewersQuery);
          const uniqueSessions = new Set();
          activeViewersSnapshot.docs.forEach(doc => {
            uniqueSessions.add(doc.data().sessionId);
          });

          // Vues d'aujourd'hui
          const todayViewsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('eventType', '==', 'page_view'),
            where('timestamp', '>=', Timestamp.fromDate(todayStart))
          );

          const todayViewsSnapshot = await getDocs(todayViewsQuery);
          const todayUsers = new Set();
          todayViewsSnapshot.docs.forEach(doc => {
            const data = doc.data();
            todayUsers.add(data.userId || data.sessionId);
          });

          // Actions récentes
          const recentActionsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            orderBy('timestamp', 'desc'),
            limit(20)
          );

          const recentActionsSnapshot = await getDocs(recentActionsQuery);
          const recentActions: AnalyticsEvent[] = recentActionsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
                id: doc.id,
                eventType: data.eventType,
                eventId: data.eventId,
                userId: data.userId,
                sessionId: data.sessionId,
                timestamp: data.timestamp?.toDate() || new Date(), // Convert Firestore timestamp
                pageUrl: data.pageUrl,
                referrer: data.referrer,
                userAgent: data.userAgent,
                country: data.country,
                city: data.city,
                device: data.device,
                browser: data.browser,
                metadata: data.metadata,
            } as AnalyticsEvent;
            });

          // Top referrers
          const referrerCounts: Record<string, number> = {};
          todayViewsSnapshot.docs.forEach(doc => {
            const referrer = doc.data().referrer || 'direct';
            referrerCounts[referrer] = (referrerCounts[referrer] || 0) + 1;
          });

          const topReferrers = Object.entries(referrerCounts)
            .map(([source, count]) => ({ source, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);

          const realTimeData: RealTimeData = {
            eventId,
            currentViewers: uniqueSessions.size,
            todayViews: todayViewsSnapshot.docs.length,
            todayUsers: todayUsers.size,
            recentActions,
            topReferrers,
          };

          return { data: realTimeData };
        } catch (error: any) {
          console.error('Error fetching real-time analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['RealTime'],
    }),

    // Entonnoir de conversion
    getConversionFunnel: builder.query<ConversionFunnel, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          const endDate = dateRange?.end || new Date();
          const startDate = dateRange?.start || new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

          const eventsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const eventsSnapshot = await getDocs(eventsQuery);
          
          let pageViews = 0;
          let ticketViews = 0;
          let ticketAdds = 0;
          let checkoutStarts = 0;
          let checkoutCompletes = 0;

          eventsSnapshot.docs.forEach(doc => {
            const { eventType } = doc.data();
            switch (eventType) {
              case 'page_view': pageViews++; break;
              case 'ticket_view': ticketViews++; break;
              case 'ticket_add': ticketAdds++; break;
              case 'checkout_start': checkoutStarts++; break;
              case 'checkout_complete': checkoutCompletes++; break;
            }
          });

          const conversionRates = {
            viewToTicket: pageViews > 0 ? (ticketViews / pageViews) * 100 : 0,
            ticketToAdd: ticketViews > 0 ? (ticketAdds / ticketViews) * 100 : 0,
            addToCheckout: ticketAdds > 0 ? (checkoutStarts / ticketAdds) * 100 : 0,
            checkoutToComplete: checkoutStarts > 0 ? (checkoutCompletes / checkoutStarts) * 100 : 0,
            overall: pageViews > 0 ? (checkoutCompletes / pageViews) * 100 : 0,
          };

          const funnel: ConversionFunnel = {
            eventId,
            pageViews,
            ticketViews,
            ticketAdds,
            checkoutStarts,
            checkoutCompletes,
            conversionRates,
          };

          return { data: funnel };
        } catch (error: any) {
          console.error('Error fetching conversion funnel:', error);
          return { error: error.message };
        }
      },
      providesTags: ['ConversionFunnel'],
    }),

    // Analytics de partage
    getShareAnalytics: builder.query<ShareAnalytics, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          const endDate = dateRange?.end || new Date();
          const startDate = dateRange?.start || new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);

          // Récupérer les partages
          const sharesQuery = query(
            collection(DB, 'sharing', 'events', 'shares'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const sharesSnapshot = await getDocs(sharesQuery);
          const shares = sharesSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          // Récupérer les conversions
          const conversionsQuery = query(
            collection(DB, 'sharing', 'conversions', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const conversionsSnapshot = await getDocs(conversionsQuery);
          const conversions = conversionsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          // Traitement des données
          const shareAnalytics = processShareAnalytics(shares, conversions, eventId);

          return { data: shareAnalytics };
        } catch (error: any) {
          console.error('Error fetching share analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Sharing'],
    }),

    // Session Flow Analytics
    getSessionFlowAnalytics: builder.query<SessionFlowAnalytics, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          const endDate = dateRange?.end || new Date();
          const startDate = dateRange?.start || new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

          const eventsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const eventsSnapshot = await getDocs(eventsQuery);
          const events = eventsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          const sessionFlowData = processSessionFlowData(events, eventId);
          return { data: sessionFlowData };
        } catch (error: any) {
          console.error('Error fetching session flow analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Analytics'],
    }),

    // Checkout Analytics
    getCheckoutAnalytics: builder.query<CheckoutAnalytics, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          const endDate = dateRange?.end || new Date();
          const startDate = dateRange?.start || new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

          const eventsQuery = query(
            collection(DB, 'analytics', 'events', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const eventsSnapshot = await getDocs(eventsQuery);
          const events = eventsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          const checkoutData = processCheckoutAnalyticsData(events, eventId);
          return { data: checkoutData };
        } catch (error: any) {
          console.error('Error fetching checkout analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Analytics'],
    }),

    // Payment Analytics
    getPaymentAnalytics: builder.query<PaymentAnalytics, { eventId: string; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, dateRange }) {
        try {
          const endDate = dateRange?.end || new Date();
          const startDate = dateRange?.start || new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);

          // Query payment events
          const paymentQuery = query(
            collection(DB, 'analytics', 'payments', 'raw'),
            where('eventId', '==', eventId),
            where('timestamp', '>=', Timestamp.fromDate(startDate)),
            where('timestamp', '<=', Timestamp.fromDate(endDate))
          );

          const paymentSnapshot = await getDocs(paymentQuery);
          const payments = paymentSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate() || new Date(),
            };
          });

          const paymentData = processPaymentAnalyticsData(payments, eventId);
          return { data: paymentData };
        } catch (error: any) {
          console.error('Error fetching payment analytics:', error);
          return { error: error.message };
        }
      },
      providesTags: ['Analytics'],
    }),

    // Exporter les données analytics
    exportAnalytics: builder.mutation<{ downloadUrl: string }, { eventId: string; format: 'csv' | 'json'; dateRange?: { start: Date; end: Date } }>({
      async queryFn({ eventId, format, dateRange }) {
        try {
          // Utiliser Firebase Functions pour l'export
          const exportFunction = httpsCallable(FUNCTIONS, 'exportAnalytics');
          const result = await exportFunction({
            eventId,
            format,
            dateRange: dateRange ? {
              start: dateRange.start.toISOString(),
              end: dateRange.end.toISOString(),
            } : undefined,
          });

          return { data: result.data as { downloadUrl: string } };
        } catch (error: any) {
          console.error('Error exporting analytics:', error);
          return { error: error.message };
        }
      },
    }),
  }),
});

// Fonctions utilitaires pour traiter les données
async function processAnalyticsOverview(
  events: any[], 
  eventId: string, 
  startDate: Date, 
  endDate: Date, 
  filters?: AnalyticsFilters
): Promise<AnalyticsOverview> {
  // Filtrer les événements selon les critères
  let filteredEvents = events;

  if (filters?.countries?.length) {
    filteredEvents = filteredEvents.filter(e => filters.countries!.includes(e.country));
  }

  if (filters?.devices?.length) {
    filteredEvents = filteredEvents.filter(e => filters.devices!.includes(e.device));
  }

  if (filters?.userType && filters.userType !== 'all') {
    if (filters.userType === 'authenticated') {
      filteredEvents = filteredEvents.filter(e => e.userId);
    } else if (filters.userType === 'anonymous') {
      filteredEvents = filteredEvents.filter(e => !e.userId);
    }
  }

  // Calculer les métriques
  const pageViews = new Set();
  const uniqueUsers = new Set();
  const uniqueSessions = new Set();
  const countryCounts: Record<string, number> = {};
  const deviceCounts = { mobile: 0, tablet: 0, desktop: 0 };
  const dailyStats: Record<string, any> = {};

  // Traffic sources tracking
  const trafficSourceCounts = {
    direct: 0,
    organicSearch: 0,
    paidSearch: 0,
    socialOrganic: 0,
    socialPaid: 0,
    email: 0,
    referral: 0,
    other: 0,
  };
  const sourceBreakdown: Record<string, { type: string; count: number }> = {};
  const processedSessions = new Set<string>(); // Track sessions to avoid double counting

  // New metrics tracking
  let newVisitors = 0;
  let returningVisitors = 0;
  const landingPageCounts: Record<string, number> = {};
  const socialPlatformCounts: Record<string, number> = {};
  const referrerDomainCounts: Record<string, number> = {};

  let totalRevenue = 0;
  let checkoutCompletes = 0;
  let authenticatedUsers = 0;
  let totalSessionDuration = 0;
  let sessionCount = 0;

  filteredEvents.forEach(event => {
    const date = event.timestamp.toISOString().split('T')[0];

    if (event.eventType === 'page_view') {
      pageViews.add(event.sessionId + '_' + event.timestamp);
      uniqueSessions.add(event.sessionId);

      if (event.userId) {
        uniqueUsers.add(event.userId);
        authenticatedUsers++;
      } else {
        uniqueUsers.add(event.sessionId);
      }

      // Statistiques géographiques
      if (event.country) {
        countryCounts[event.country] = (countryCounts[event.country] || 0) + 1;
      }

      // Statistiques d'appareils
      if (event.device && deviceCounts.hasOwnProperty(event.device)) {
        deviceCounts[event.device as keyof typeof deviceCounts]++;
      }

      // Session duration
      if (event.metadata?.timeOnSite) {
        totalSessionDuration += event.metadata.timeOnSite;
        sessionCount++;
      }

      // Traffic sources - only count once per session
      if (!processedSessions.has(event.sessionId)) {
        processedSessions.add(event.sessionId);

        // Extract traffic source from referralData
        const referralData = event.referralData;

        // Track new vs returning visitors
        if (referralData?.isFirstVisit === true) {
          newVisitors++;
        } else if (referralData?.isFirstVisit === false) {
          returningVisitors++;
        } else {
          // If no referralData, count as returning (conservative approach)
          returningVisitors++;
        }

        // Track landing pages
        if (referralData?.landingPath) {
          const landingPath = referralData.landingPath;
          landingPageCounts[landingPath] = (landingPageCounts[landingPath] || 0) + 1;
        } else if (event.pageUrl) {
          try {
            const url = new URL(event.pageUrl);
            landingPageCounts[url.pathname] = (landingPageCounts[url.pathname] || 0) + 1;
          } catch {
            // Invalid URL, skip
          }
        }

        // Track referrer domains
        if (referralData?.referrerDomain) {
          const domain = referralData.referrerDomain.toLowerCase();
          referrerDomainCounts[domain] = (referrerDomainCounts[domain] || 0) + 1;
        } else if (referralData?.fullReferrer) {
          try {
            const url = new URL(referralData.fullReferrer);
            const domain = url.hostname.toLowerCase();
            referrerDomainCounts[domain] = (referrerDomainCounts[domain] || 0) + 1;
          } catch {
            // Invalid URL, skip
          }
        } else if (event.referrer) {
          try {
            const url = new URL(event.referrer);
            const domain = url.hostname.toLowerCase();
            referrerDomainCounts[domain] = (referrerDomainCounts[domain] || 0) + 1;
          } catch {
            // Invalid URL, skip
          }
        }

        // Track social platforms from firstTouchSource or trafficSource
        const firstTouchSource = referralData?.firstTouchSource || '';
        const trafficSourceType = referralData?.trafficSource?.type || '';
        if (firstTouchSource.includes('social') || trafficSourceType.includes('social')) {
          let platform = referralData?.trafficSource?.detectedPlatform ||
                         referralData?.trafficSource?.source ||
                         firstTouchSource.split(':')[1] || 'unknown';
          platform = platform.toLowerCase();
          socialPlatformCounts[platform] = (socialPlatformCounts[platform] || 0) + 1;
        }
        if (referralData?.trafficSource?.type || referralData?.firstTouchSource) {
          // Parse firstTouchSource format: "type:source" (e.g., "social_organic:instagram")
          let sourceType = referralData.trafficSource?.type;
          let sourceName = referralData.trafficSource?.source || referralData.trafficSource?.detectedPlatform;

          // Fallback to firstTouchSource if trafficSource is not available
          if (!sourceType && referralData.firstTouchSource) {
            const [type, source] = referralData.firstTouchSource.split(':');
            sourceType = type;
            sourceName = source || type;
          }

          // Map source type to our categories
          switch (sourceType) {
            case 'direct':
              trafficSourceCounts.direct++;
              break;
            case 'organic_search':
              trafficSourceCounts.organicSearch++;
              break;
            case 'paid_search':
              trafficSourceCounts.paidSearch++;
              break;
            case 'social_organic':
              trafficSourceCounts.socialOrganic++;
              break;
            case 'social_paid':
              trafficSourceCounts.socialPaid++;
              break;
            case 'email':
              trafficSourceCounts.email++;
              break;
            case 'referral':
            case 'affiliate':
            case 'partner':
              trafficSourceCounts.referral++;
              break;
            default:
              trafficSourceCounts.other++;
          }

          // Track individual source breakdown
          if (sourceName) {
            const key = sourceName.toLowerCase();
            if (!sourceBreakdown[key]) {
              sourceBreakdown[key] = { type: sourceType || 'unknown', count: 0 };
            }
            sourceBreakdown[key].count++;
          }
        } else if (event.referrer) {
          // Fallback: categorize by referrer if no referralData
          const referrer = event.referrer.toLowerCase();
          if (!referrer || referrer === '') {
            trafficSourceCounts.direct++;
          } else if (referrer.includes('google.') || referrer.includes('bing.') || referrer.includes('yahoo.')) {
            trafficSourceCounts.organicSearch++;
            const domain = referrer.split('/')[0];
            if (!sourceBreakdown[domain]) {
              sourceBreakdown[domain] = { type: 'organic_search', count: 0 };
            }
            sourceBreakdown[domain].count++;
          } else if (referrer.includes('facebook.') || referrer.includes('instagram.') ||
                     referrer.includes('twitter.') || referrer.includes('linkedin.') ||
                     referrer.includes('tiktok.') || referrer.includes('l.instagram.com')) {
            trafficSourceCounts.socialOrganic++;
            const domain = referrer.includes('instagram') ? 'instagram' :
                           referrer.includes('facebook') ? 'facebook' :
                           referrer.includes('twitter') ? 'twitter' :
                           referrer.includes('linkedin') ? 'linkedin' :
                           referrer.includes('tiktok') ? 'tiktok' : referrer.split('/')[0];
            if (!sourceBreakdown[domain]) {
              sourceBreakdown[domain] = { type: 'social_organic', count: 0 };
            }
            sourceBreakdown[domain].count++;
          } else {
            trafficSourceCounts.referral++;
            const domain = referrer.split('/')[0];
            if (!sourceBreakdown[domain]) {
              sourceBreakdown[domain] = { type: 'referral', count: 0 };
            }
            sourceBreakdown[domain].count++;
          }
        } else {
          trafficSourceCounts.direct++;
        }
      }
    }

    if (event.eventType === 'checkout_complete') {
      checkoutCompletes++;
      if (event.metadata?.totalAmount) {
        totalRevenue += event.metadata.totalAmount;
      }
    }

    // Statistiques quotidiennes
    if (!dailyStats[date]) {
      dailyStats[date] = { date, views: 0, users: new Set(), conversions: 0, revenue: 0 };
    }

    if (event.eventType === 'page_view') {
      dailyStats[date].views++;
      dailyStats[date].users.add(event.userId || event.sessionId);
    }

    if (event.eventType === 'checkout_complete') {
      dailyStats[date].conversions++;
      dailyStats[date].revenue += event.metadata?.totalAmount || 0;
    }
  });

  // Traitement final des statistiques quotidiennes
  const processedDailyStats = Object.values(dailyStats).map((day: any) => ({
    ...day,
    users: day.users.size,
  }));

  const totalViews = pageViews.size;
  const totalUsers = uniqueUsers.size;
  const conversionRate = totalViews > 0 ? (checkoutCompletes / totalViews) * 100 : 0;
  const averageOrderValue = checkoutCompletes > 0 ? totalRevenue / checkoutCompletes : 0;
  const bounceRate = uniqueSessions.size > 0 ? ((uniqueSessions.size - filteredEvents.filter(e => e.eventType === 'ticket_view').length) / uniqueSessions.size) * 100 : 0;
  const averageSessionDuration = sessionCount > 0 ? totalSessionDuration / sessionCount : 0;

  const topCountries = Object.entries(countryCounts)
    .map(([country, count]) => ({ country, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Build traffic sources breakdown with percentages
  const totalTrafficSources = Object.values(trafficSourceCounts).reduce((a, b) => a + b, 0);
  const trafficSourcesBreakdown = Object.entries(sourceBreakdown)
    .map(([source, data]) => ({
      source,
      type: data.type,
      count: data.count,
      percentage: totalTrafficSources > 0 ? (data.count / totalTrafficSources) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Build top landing pages with percentages
  const totalLandingPages = Object.values(landingPageCounts).reduce((a, b) => a + b, 0);
  const topLandingPages = Object.entries(landingPageCounts)
    .map(([path, count]) => ({
      path,
      count,
      percentage: totalLandingPages > 0 ? (count / totalLandingPages) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Build social platforms breakdown with percentages
  const totalSocialPlatforms = Object.values(socialPlatformCounts).reduce((a, b) => a + b, 0);
  const socialPlatforms = Object.entries(socialPlatformCounts)
    .map(([platform, count]) => ({
      platform,
      count,
      percentage: totalSocialPlatforms > 0 ? (count / totalSocialPlatforms) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Build top referrer domains with percentages
  const totalReferrerDomains = Object.values(referrerDomainCounts).reduce((a, b) => a + b, 0);
  const topReferrerDomains = Object.entries(referrerDomainCounts)
    .map(([domain, count]) => ({
      domain,
      count,
      percentage: totalReferrerDomains > 0 ? (count / totalReferrerDomains) * 100 : 0,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return {
    eventId,
    dateRange: { start: startDate, end: endDate },
    totalViews,
    uniqueViews: uniqueSessions.size,
    totalUsers,
    authenticatedUsers,
    conversionRate,
    totalRevenue,
    averageOrderValue,
    bounceRate,
    averageSessionDuration,
    topCountries,
    topDevices: deviceCounts,
    trafficSources: {
      ...trafficSourceCounts,
      breakdown: trafficSourcesBreakdown,
    },
    visitorTypes: {
      newVisitors,
      returningVisitors,
    },
    topLandingPages,
    socialPlatforms,
    topReferrerDomains,
    dailyStats: processedDailyStats,
  };
}

function processShareAnalytics(shares: any[], conversions: any[], eventId: string): ShareAnalytics {
  const totalShares = shares.length;
  const uniqueSharers = new Set(shares.map(s => s.sharedBy)).size;
  
  const sharesByMethod: Record<string, number> = {};
  shares.forEach(share => {
    sharesByMethod[share.shareMethod] = (sharesByMethod[share.shareMethod] || 0) + 1;
  });

  const totalConversions = conversions.length;
  const conversionRate = totalShares > 0 ? (totalConversions / totalShares) * 100 : 0;

  const totalAttributedRevenue = conversions
    .filter(c => c.conversionType === 'checkout_complete')
    .reduce((sum, c) => sum + (c.conversionValue || 0), 0);

  // Top performers
  const sharerStats: Record<string, any> = {};
  shares.forEach(share => {
    if (!sharerStats[share.sharedBy]) {
      sharerStats[share.sharedBy] = { shares: 0, conversions: 0, revenue: 0 };
    }
    sharerStats[share.sharedBy].shares++;
  });

  conversions.forEach(conversion => {
    if (sharerStats[conversion.sharedBy]) {
      sharerStats[conversion.sharedBy].conversions++;
      if (conversion.conversionType === 'checkout_complete') {
        sharerStats[conversion.sharedBy].revenue += conversion.conversionValue || 0;
      }
    }
  });

  const topSharers = Object.entries(sharerStats)
    .map(([userId, stats]) => ({ userId, ...stats }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);

  const viralCoefficient = uniqueSharers > 0 ? totalShares / uniqueSharers : 0;
  const shareMultiplier = totalShares > 0 ? totalConversions / totalShares : 0;

  return {
    eventId,
    totalShares,
    uniqueSharers,
    sharesByMethod,
    totalConversions,
    conversionRate,
    totalAttributedRevenue,
    topSharers,
    viralCoefficient,
    shareMultiplier,
  };
}

function processSessionFlowData(events: any[], eventId: string): SessionFlowAnalytics {
  let eventPageViews = 0;
  let checkoutEntries = 0;
  let participantStepCompletes = 0;
  let paymentStepEntries = 0;
  let paymentSuccesses = 0;
  let confirmationViews = 0;
  
  const sessionDurations = { short: 0, medium: 0, long: 0, veryLong: 0, total: 0 };
  const dropoffReasons = { eventPageExit: 0, checkoutAbandonment: 0, paymentFailure: 0, validationErrors: 0 };
  
  let totalSessionDuration = 0;
  let completedSessions = 0;
  let abandonedSessions = 0;
  const uniqueSessions = new Set();

  events.forEach(event => {
    uniqueSessions.add(event.sessionId);
    
    switch (event.eventType) {
      case 'page_view': eventPageViews++; break;
      case 'checkout_start': checkoutEntries++; break;
      case 'participant_info_complete': participantStepCompletes++; break;
      case 'payment_step_entry': paymentStepEntries++; break;
      case 'payment_success': paymentSuccesses++; break;
      case 'confirmation_view': confirmationViews++; break;
      case 'checkout_complete': completedSessions++; break;
      case 'checkout_abandon': abandonedSessions++; break;
    }

    if (event.metadata?.sessionDuration) {
      const duration = event.metadata.sessionDuration;
      totalSessionDuration += duration;
      
      if (duration < 30) sessionDurations.short++;
      else if (duration < 120) sessionDurations.medium++;
      else if (duration < 300) sessionDurations.long++;
      else sessionDurations.veryLong++;
      
      sessionDurations.total++;
    }

    if (event.metadata?.dropoffReason) {
      dropoffReasons[event.metadata.dropoffReason as keyof typeof dropoffReasons]++;
    }
  });

  return {
    eventId,
    eventPageViews,
    checkoutEntries,
    participantStepCompletes,
    paymentStepEntries,
    paymentSuccesses,
    confirmationViews,
    sessionDurations,
    averageSessionDuration: sessionDurations.total > 0 ? totalSessionDuration / sessionDurations.total : 0,
    completedSessions,
    abandonedSessions,
    totalSessions: uniqueSessions.size,
    dropoffReasons,
  };
}

function processCheckoutAnalyticsData(events: any[], eventId: string): CheckoutAnalytics {
  let checkoutStarts = 0;
  let formCompletions = 0;
  let paymentAttempts = 0;
  let checkoutCompletions = 0;

  const stepAnalysis: Array<{ step: string; users: number; completions: number; dropoffs: number }> = [
    { step: 'Checkout Start', users: 0, completions: 0, dropoffs: 0 },
    { step: 'Form Completion', users: 0, completions: 0, dropoffs: 0 },
    { step: 'Payment Attempt', users: 0, completions: 0, dropoffs: 0 },
    { step: 'Checkout Complete', users: 0, completions: 0, dropoffs: 0 },
  ];

  const checkoutSteps: Array<{
    stepName: string;
    usersReached: number;
    completed: number;
    averageTime: number;
    successRate: number;
    commonErrors: string[];
  }> = [
    { stepName: 'Participant Info', usersReached: 0, completed: 0, averageTime: 0, successRate: 0, commonErrors: [] },
    { stepName: 'Payment Details', usersReached: 0, completed: 0, averageTime: 0, successRate: 0, commonErrors: [] },
    { stepName: 'Confirmation', usersReached: 0, completed: 0, averageTime: 0, successRate: 0, commonErrors: [] },
  ];

  events.forEach(event => {
    switch (event.eventType) {
      case 'checkout_start':
        checkoutStarts++;
        stepAnalysis[0].users++;
        break;
      case 'form_complete':
        formCompletions++;
        stepAnalysis[1].completions++;
        break;
      case 'payment_attempt':
        paymentAttempts++;
        stepAnalysis[2].users++;
        break;
      case 'checkout_complete':
        checkoutCompletions++;
        stepAnalysis[3].completions++;
        break;
    }

    // Process step data
    if (event.metadata?.stepName) {
      const step = checkoutSteps.find(s => s.stepName === event.metadata.stepName);
      if (step) {
        step.usersReached++;
        if (event.eventType === 'step_complete') {
          step.completed++;
        }
        if (event.metadata?.timeSpent) {
          step.averageTime = (step.averageTime + event.metadata.timeSpent) / 2;
        }
      }
    }
  });

  // Calculate success rates
  checkoutSteps.forEach(step => {
    step.successRate = step.usersReached > 0 ? (step.completed / step.usersReached) * 100 : 0;
  });

  return {
    eventId,
    checkoutStarts,
    formCompletions,
    paymentAttempts,
    checkoutCompletions,
    stepAnalysis,
    checkoutSteps,
  };
}

function processPaymentAnalyticsData(payments: any[], eventId: string): PaymentAnalytics {
  let totalPaymentAttempts = 0;
  let successfulPayments = 0;
  let failedPayments = 0;
  let cancelledPayments = 0;
  let totalAmount = 0;

  const failureCounts: Record<string, number> = {};

  payments.forEach(payment => {
    totalPaymentAttempts++;
    
    switch (payment.status) {
      case 'success':
        successfulPayments++;
        if (payment.amount) totalAmount += payment.amount;
        break;
      case 'failed':
        failedPayments++;
        if (payment.failureReason) {
          failureCounts[payment.failureReason] = (failureCounts[payment.failureReason] || 0) + 1;
        }
        break;
      case 'cancelled':
        cancelledPayments++;
        break;
    }
  });

  const failureReasons = Object.entries(failureCounts).map(([reason, count]) => ({
    reason,
    count,
    percentage: totalPaymentAttempts > 0 ? (count / totalPaymentAttempts) * 100 : 0,
  }));

  return {
    eventId,
    totalPaymentAttempts,
    successfulPayments,
    failedPayments,
    cancelledPayments,
    averagePaymentAmount: successfulPayments > 0 ? totalAmount / successfulPayments : 0,
    failureReasons,
  };
}

export const {
  useGetAnalyticsOverviewQuery,
  useGetRealTimeAnalyticsQuery,
  useGetConversionFunnelQuery,
  useGetShareAnalyticsQuery,
  useGetSessionFlowAnalyticsQuery,
  useGetCheckoutAnalyticsQuery,
  useGetPaymentAnalyticsQuery,
  useExportAnalyticsMutation,
} = AnalyticsApi;